
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { KolAnalyticsTable } from "@/components/analytics/KolAnalyticsTable";
import { MonthlyExpenseTable } from "@/components/analytics/MonthlyExpenseTable";
import { ContentIdAnalytics } from "@/components/analytics/ContentIdAnalytics";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, Users, BarChart3 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BuExpenseTable } from "@/components/charts/BuExpenseTable";
import { ExpenseByCountryChart } from "@/components/charts/ExpenseByCountryChart";
import { ExpenseByQuarterTable } from "@/components/charts/ExpenseByQuarterTable";
import { QuarterlyReportTable } from "@/components/charts/QuarterlyReportTable";
import { supabase } from "@/integrations/supabase/client";

const KolReports = () => {
  const [userAccess, setUserAccess] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [kolTrackerData, setKolTrackerData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  // State for collapsible cards
  const [contentIdAnalyticsOpen, setContentIdAnalyticsOpen] = useState(true);
  const [kolAnalyticsOpen, setKolAnalyticsOpen] = useState(true);
  const [monthlyExpenseOpen, setMonthlyExpenseOpen] = useState(true);
  const [expenseGraphOpen, setExpenseGraphOpen] = useState(true);
  const [quarterlyReportOpen, setQuarterlyReportOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("kols-posting");

  // Get user access details
  useEffect(() => {
    const access = localStorage.getItem('dashboard_user_access');
    const userId = localStorage.getItem('dashboard_user_id');
    const userName = localStorage.getItem('dashboard_user_name');
    
    console.log("User access level:", access);
    console.log("User ID:", userId);
    console.log("User name:", userName);
    
    setUserAccess(access);
    setUserName(userName);
  }, []);

  // Fetch kol_tracker data for regional reports
  useEffect(() => {
    const fetchKolTrackerData = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('kol_tracker')
          .select('expense, campaign_bu, language, posting_date')
          .not('expense', 'is', null);
        
        if (error) {
          throw error;
        }
        
        setKolTrackerData(data || []);
      } catch (error) {
        console.error("Error fetching KOL tracker data:", error);
        toast({
          title: "Failed to load regional data",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab === "kols-regional") {
      fetchKolTrackerData();
    }
  }, [activeTab, toast]);

  const hasFullAccess = userAccess === 'full-access';

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-3xl font-bold text-foreground">KOL's REPORTS</h1>
      
      {!hasFullAccess && (
        <div className={`text-sm ${isLightMode ? 'text-gray-600' : 'text-[#8E9196]'} italic`}>
          Limited access mode: You can only view analytics for your own KOL records
        </div>
      )}
      
      <Tabs 
        defaultValue="kols-posting" 
        className="w-full"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="border border-border bg-transparent">
          <TabsTrigger 
            value="kols-posting"
            className="tabs-trigger"
          >
            <Users className="h-4 w-4" />
            KOL's Posting
          </TabsTrigger>
          <TabsTrigger 
            value="kols-regional"
            className="tabs-trigger"
          >
            <BarChart3 className="h-4 w-4" />
            KOL's & Regional Report
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="kols-posting" className="space-y-6 mt-4">
          <div className="grid grid-cols-1 gap-6">
            {/* Content ID Analytics Section with Description */}
            <Collapsible open={contentIdAnalyticsOpen} onOpenChange={setContentIdAnalyticsOpen} className="w-full">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
                  <div className="flex-1">
                    <CardTitle className={`text-xl font-bold ${isLightMode ? "text-gray-800" : ""}`}>CONTENT ID ANALYTICS</CardTitle>
                    <p className={`text-sm mt-1 ${isLightMode ? "text-gray-600" : "text-gray-400"}`}>
                      Comprehensive analytics for content campaigns including KOL metrics, views, expenses, and regional performance
                    </p>
                  </div>
                  <CollapsibleTrigger asChild>
                    <div className="hover:bg-muted p-1 rounded-full">
                      {contentIdAnalyticsOpen ? (
                        <ChevronUp className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="p-0">
                    <ContentIdAnalytics />
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* KOL Analytics moved to second position */}
            <Collapsible open={kolAnalyticsOpen} onOpenChange={setKolAnalyticsOpen} className="w-full">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
                  <CardTitle className={`text-xl font-bold ${isLightMode ? "text-gray-800" : ""}`}>KOL ANALYTICS</CardTitle>
                  <CollapsibleTrigger asChild>
                    <div className="hover:bg-muted p-1 rounded-full">
                      {kolAnalyticsOpen ? (
                        <ChevronUp className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="p-4">
                    <KolAnalyticsTable />
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Monthly Expense Table - Moved to third position */}
            <Collapsible open={monthlyExpenseOpen} onOpenChange={setMonthlyExpenseOpen} className="w-full">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
                  <CardTitle className={`text-xl font-bold ${isLightMode ? "text-gray-800" : ""}`}>MONTHLY EXPENSE ANALYSIS</CardTitle>
                  <CollapsibleTrigger asChild>
                    <div className="hover:bg-muted p-1 rounded-full">
                      {monthlyExpenseOpen ? (
                        <ChevronUp className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="p-4">
                    <MonthlyExpenseTable />
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>
        </TabsContent>
        
        <TabsContent value="kols-regional" className="space-y-6 mt-4">
          <div className="grid grid-cols-1 gap-6">
            {/* New Quarterly Report Section */}
            <Collapsible open={quarterlyReportOpen} onOpenChange={setQuarterlyReportOpen} className="w-full">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
                  <CardTitle className={`text-xl font-bold ${isLightMode ? "text-gray-800" : ""}`}>QUARTERLY REPORT</CardTitle>
                  <CollapsibleTrigger asChild>
                    <div className="hover:bg-muted p-1 rounded-full">
                      {quarterlyReportOpen ? (
                        <ChevronUp className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="p-4">
                    {isLoading ? (
                      <div className="flex items-center justify-center h-48">
                        <p className="text-muted-foreground">Loading quarterly report data...</p>
                      </div>
                    ) : (
                      <QuarterlyReportTable />
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
            
            {/* Expense Graph Section */}
            <Collapsible open={expenseGraphOpen} onOpenChange={setExpenseGraphOpen} className="w-full">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
                  <CardTitle className={`text-xl font-bold ${isLightMode ? "text-gray-800" : ""}`}>EXPENSE GRAPH</CardTitle>
                  <CollapsibleTrigger asChild>
                    <div className="hover:bg-muted p-1 rounded-full">
                      {expenseGraphOpen ? (
                        <ChevronUp className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 ${isLightMode ? "text-gray-500" : "text-dashboard-text-secondary"}`} />
                      )}
                    </div>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="p-4">
                    {isLoading ? (
                      <div className="flex items-center justify-center h-48">
                        <p className="text-muted-foreground">Loading expense data...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className={`text-lg font-semibold mb-4 ${isLightMode ? "text-gray-800" : ""}`}>Expense By Country</h3>
                          <ExpenseByCountryChart data={kolTrackerData} />
                        </div>
                        <div>
                          <h3 className={`text-lg font-semibold mb-4 ${isLightMode ? "text-gray-800" : ""}`}>Expense By Quarter</h3>
                          <div className="overflow-x-auto">
                            <ExpenseByQuarterTable data={kolTrackerData} />
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default KolReports;
